import classNames from 'classnames'
import moment from 'moment'
import { toLower, trim } from 'lodash'
import { formatPrice } from 'lib/utils'
import { CalendarDaysIcon, ClipboardDocumentIcon } from '@heroiconsv2/react/24/outline'
import { LABEL, PANEL, ROW, ROW_START, SectionCard } from './BookingSection'
import {
  getBookingCoupon,
  getCouponCode,
  getCouponTypeTranslation,
  getCouponValueDisplay,
  getCreatedByTranslation,
  getDeliveryTypeTranslation,
  getDiscountSourceBadgeClass,
  getDiscountSourceTranslation,
  getPaymentMethodTranslation,
  withCardGateway,
  getPaymentStatusTranslation,
  getPlatformTranslation,
  getStatusTranslation,
  getInsuranceDisplay,
  getInsuranceTypeLabel,
} from '../bookingDetailHelpers'
import BookingInvoiceList from './BookingInvoiceList'

const BookingDetailsCard = ({ bookingData, t, orderStatus, onCopyBookingId, hasDeliveryDetails }) => (
  <SectionCard icon={CalendarDaysIcon} title={t('booking.sections.bookingDetails')}>
    <div className={PANEL}>
      <div className={ROW}>
        <div className={LABEL}>{t('booking.labels.bookingId')}</div>
        <div className="flex items-center gap-2">
          <div className="text-sm sm:text-base text-slate-800 font-mono break-all">{bookingData?._id || '-'}</div>
          {bookingData?._id && (
            <button
              type="button"
              onClick={() => onCopyBookingId(bookingData._id)}
              className="flex-shrink-0 rounded-lg border border-slate-200/90 bg-white p-1.5 text-slate-500 shadow-sm transition hover:border-slate-300 hover:bg-slate-50 hover:text-slate-800"
              title="Copy Booking ID"
            >
              <ClipboardDocumentIcon className="w-4 h-4" />
            </button>
          )}
        </div>
      </div>

      <div className={ROW}>
        <div className={LABEL}>{t('booking.labels.bookingReferenceId', { defaultValue: t('booking.labels.referenceId') })}</div>
        <div className="text-sm sm:text-base text-slate-800">{bookingData?.bookingReferenceId || '-'}</div>
      </div>

      {hasDeliveryDetails && (
        <div className={ROW}>
          <div className={LABEL}>{t('booking.labels.bookingType')}</div>
          <div className="text-sm sm:text-base text-slate-800">{getDeliveryTypeTranslation(t, bookingData?.delivery?.type)}</div>
        </div>
      )}

      <div className={ROW}>
        <div className={LABEL}>{t('booking.labels.paymentStatus')}</div>
        <div
          className={`text-xs font-semibold tracking-wide px-2.5 py-1 rounded-full inline-flex items-center shadow-sm ${
            bookingData?.paymentStatus === 'paid'
              ? 'bg-emerald-100 text-emerald-800 ring-1 ring-emerald-200/60'
              : bookingData?.paymentStatus === 'failure'
              ? 'bg-red-100 text-red-800 ring-1 ring-red-200/60'
              : bookingData?.paymentStatus === 'unpaid'
              ? 'bg-amber-100 text-amber-900 ring-1 ring-amber-200/60'
              : 'bg-slate-200 text-slate-700 ring-1 ring-slate-300/50'
          }`}
        >
          {getPaymentStatusTranslation(t, bookingData?.paymentStatus)}
        </div>
      </div>

      <div className={ROW}>
        <div className={LABEL}>{t('booking.labels.bookingStatus')}</div>
        <div
          className={classNames('text-xs font-semibold tracking-wide px-2.5 py-1 rounded-full inline-flex items-center shadow-sm ring-1', {
            'bg-slate-200 text-slate-800 ring-slate-300/60': bookingData?.status === 'pending',
            'bg-rose-500 text-white ring-rose-600/30': bookingData?.status === 'cancelled' || bookingData?.status === 'rejected' || bookingData?.status === 'failedPayment',
            'bg-emerald-600 text-white ring-emerald-700/25': bookingData?.status === 'completed' || bookingData?.status === 'confirmed',
          })}
        >
          {bookingData?.isOfficeBlock ? t('booking.status.officeBlock') : getStatusTranslation(t, orderStatus)}
        </div>
      </div>

      {trim(String(bookingData?.cancellationReason || '')) !== '' && (
        <div className={ROW_START}>
          <div className={LABEL}>{t('booking.labels.cancellationReason')}</div>
          <div className="text-sm sm:text-base font-medium text-rose-700 sm:text-right break-words whitespace-pre-line">{trim(bookingData.cancellationReason)}</div>
        </div>
      )}

      {trim(String(bookingData?.cancellationNotes || '')) !== '' && (
        <div className={ROW_START}>
          <div className={LABEL}>{t('booking.labels.cancellationNotes')}</div>
          <div className="text-sm sm:text-base text-slate-800 sm:text-right break-words whitespace-pre-line">{trim(bookingData.cancellationNotes)}</div>
        </div>
      )}

      {bookingData?.platform && (
        <div className={ROW}>
          <div className={LABEL}>{t('booking.labels.platform')}</div>
          <div className="text-sm sm:text-base text-slate-800">{getPlatformTranslation(t, bookingData.platform)}</div>
        </div>
      )}

      {getInsuranceDisplay(bookingData).hasSplitType && (
        <div className={ROW}>
          <div className={LABEL}>{t('booking.labels.insuranceType')}</div>
          <div className="flex flex-col items-end gap-0.5">
            <span className="text-sm sm:text-base text-slate-800">{getInsuranceTypeLabel(bookingData, t)}</span>
            <span className={classNames('text-xs', getInsuranceDisplay(bookingData).collectAtOffice ? 'text-amber-600' : 'text-emerald-600')}>
              {getInsuranceDisplay(bookingData).collectAtOffice ? t('booking.orderSummary.insuranceCollectAtOffice') : t('booking.orderSummary.insurancePaidOnline')}
            </span>
          </div>
        </div>
      )}

      <div className={ROW}>
        <div className={LABEL}>{t('booking.labels.bookingCreatedBy')}</div>
        <div className="flex flex-col items-end sm:items-end gap-1">
          <div
            className={`text-xs font-semibold tracking-wide px-2.5 py-1 rounded-full inline-flex items-center shadow-sm ring-1 ${
              toLower(bookingData?.bookingCreatedBy) === 'user'
                ? 'bg-emerald-100 text-emerald-800 ring-emerald-200/60'
                : toLower(bookingData?.bookingCreatedBy) === 'office'
                ? 'bg-amber-100 text-amber-900 ring-amber-200/60'
                : toLower(bookingData?.bookingCreatedBy) === 'admin'
                ? 'bg-slate-200 text-slate-800 ring-slate-300/50'
                : toLower(bookingData?.bookingCreatedBy) === 'partner'
                ? 'bg-sky-100 text-sky-800 ring-sky-200/60'
                : 'bg-slate-200 text-slate-700 ring-slate-300/50'
            }`}
          >
            {getCreatedByTranslation(t, bookingData?.bookingCreatedBy)}
          </div>
        </div>
      </div>

      <div className={ROW}>
        <div className={LABEL}>{t('booking.labels.paymentMethod')}</div>
        <div
          className={`text-xs font-semibold tracking-wide px-2.5 py-1 rounded-full inline-flex items-center shadow-sm ring-1 ${
            toLower(bookingData?.paymentMethod) === 'card'
              ? 'bg-emerald-100 text-emerald-800 ring-emerald-200/60'
              : toLower(bookingData?.paymentMethod) === 'cash' || toLower(bookingData?.paymentMethod) === 'pos'
              ? 'bg-amber-100 text-amber-900 ring-amber-200/60'
              : 'bg-slate-200 text-slate-700 ring-slate-300/50'
          }`}
        >
          {withCardGateway(getPaymentMethodTranslation(t, bookingData?.paymentMethod), bookingData)}
        </div>
      </div>

      {bookingData?.paymentSessionMetadata?.gateway === 'qpay' && (
        <>
          <div className={ROW}>
            <div className={LABEL}>{t('booking.labels.qpayState')}</div>
            <div className="text-sm sm:text-base text-slate-800">
              {bookingData.paymentSessionMetadata.qpay?.state || '-'}
              {bookingData.paymentSessionMetadata.qpay?.state === 'unknown' && ` (${t('booking.labels.qpayReconcile')})`}
            </div>
          </div>
          <div className={ROW}>
            <div className={LABEL}>{t('booking.labels.qpayLoanId')}</div>
            <div className="text-sm sm:text-base text-slate-800 break-all">{bookingData.paymentSessionMetadata.qpay?.loanId || '-'}</div>
          </div>
          <div className={ROW}>
            <div className={LABEL}>{t('booking.labels.qpayMessageId')}</div>
            <div className="text-sm sm:text-base text-slate-800 break-all">{bookingData.paymentSessionMetadata.qpay?.confirmMsgId || '-'}</div>
          </div>
        </>
      )}

      {(bookingData?.transferReferenceNumber || bookingData?.transferReceiptUrl) && (
        <>
          {bookingData?.transferReferenceNumber && (
            <div className={ROW}>
              <div className={LABEL}>{t('booking.labels.transferReferenceNumber')}</div>
              <div className="text-sm sm:text-base text-slate-800 break-all">{bookingData.transferReferenceNumber}</div>
            </div>
          )}
          {bookingData?.transferReceiptUrl && (
            <div className={ROW}>
              <div className={LABEL}>{t('booking.labels.transferReceipt')}</div>
              <a href={bookingData.transferReceiptUrl} target="_blank" rel="noopener noreferrer" className="text-sm text-blue-600 hover:underline">
                {t('booking.buttons.viewTransferReceipt')}
              </a>
            </div>
          )}
        </>
      )}

      {bookingData?.rrn && (
        <div className={ROW}>
          <div className={LABEL}>RNN</div>
          <div className="text-sm sm:text-base text-slate-800 break-all">{bookingData.rrn}</div>
        </div>
      )}

      {bookingData?.discountSource != null && trim(String(bookingData.discountSource)) !== '' && (
        <div className={ROW}>
          <div className={LABEL}>{t('booking.labels.discountSource')}</div>
          <div
            className={classNames(
              'text-xs font-semibold tracking-wide px-2.5 py-1 rounded-full inline-flex items-center shadow-sm ring-1 ring-black/5',
              getDiscountSourceBadgeClass(bookingData.discountSource)
            )}
          >
            {getDiscountSourceTranslation(t, bookingData.discountSource)}
          </div>
        </div>
      )}

      {bookingData?.extendBookingAjarleeDiscountPercentage > 0 && (
        <div className={ROW}>
          <div className={LABEL}>{t('booking.labels.extendBookingAjarleeDiscountPercentage')}</div>
          <div className="text-sm sm:text-base text-slate-800 sm:text-right tabular-nums">{formatPrice(bookingData.extendBookingAjarleeDiscountPercentage, true)}%</div>
        </div>
      )}

      {getBookingCoupon(bookingData) && (
        <>
          <div className={ROW}>
            <div className={LABEL}>{t('booking.labels.couponCode')}</div>
            <div className="text-sm sm:text-base font-semibold text-slate-800 sm:text-right break-all">{getCouponCode(getBookingCoupon(bookingData))}</div>
          </div>
          <div className={ROW}>
            <div className={LABEL}>{t('booking.labels.discountType')}</div>
            <div className="text-sm sm:text-base text-slate-800 sm:text-right">{getCouponTypeTranslation(t, getBookingCoupon(bookingData))}</div>
          </div>
          <div className={ROW}>
            <div className={LABEL}>{t('booking.labels.discountValue')}</div>
            <div className="text-sm sm:text-base font-semibold text-emerald-700 sm:text-right tabular-nums">{getCouponValueDisplay(t, getBookingCoupon(bookingData))}</div>
          </div>
        </>
      )}

      <div className={ROW}>
        <div className={LABEL}>{t('booking.labels.rentalDays')}</div>
        <div className="text-sm sm:text-base text-slate-800 sm:text-right tabular-nums">{bookingData?.numberOfDays || ''}</div>
      </div>

      <div className={ROW_START}>
        <div className={LABEL}>{t('booking.labels.bookingDate')}</div>
        <div className="text-sm sm:text-base text-slate-800 sm:text-right break-words">
          {bookingData?.bookingDate ? moment.parseZone(bookingData.bookingDate).format('MMMM Do YYYY, h:mm A') : ''}
        </div>
      </div>

      <div className={ROW_START}>
        <div className={LABEL}>{t('booking.labels.rentalStartDate')}</div>
        <div className="text-sm sm:text-base text-slate-800 sm:text-right break-words">
          {bookingData?.rentalStartDate ? moment.parseZone(bookingData.rentalStartDate).format('MMMM Do YYYY, h:mm A') : ''}
        </div>
      </div>

      <div className={ROW_START}>
        <div className={LABEL}>{t('booking.labels.rentalEndDate')}</div>
        <div className="text-sm sm:text-base text-slate-800 sm:text-right break-words">
          {bookingData?.rentalEndDate ? moment.parseZone(bookingData.rentalEndDate).format('MMMM Do YYYY, h:mm A') : ''}
        </div>
      </div>
    </div>
    <BookingInvoiceList bookingData={bookingData} t={t} />
  </SectionCard>
)

export default BookingDetailsCard
